import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import {
  AppState,
  masterDataDataSelector,
  reportFilterCountryCodeSelector,
  reportFilterTenderGroupBySelector,
  reportMissingClosingTotalMissingClosingTotalSelector,
  reportSalesTypesSelector,
  reportTenderSelector,
  downloadReportPdf,
} from '../../state';
import { TenderReport, ReportFilterTenderGroupBy } from '../../types';
import { constants } from '../../helpers';
import handlerFactory, { ReportTenderDataGridContext } from './handlerFactory';

// selector estable: evita que el componente raíz se re-renderice (el handler se crea con hooks dentro de useMemo)
const columnChooserDisabledSelector = (state: AppState) =>
  constants.isSmsReportedCountry(reportFilterCountryCodeSelector(state));

const DataGridTitle = () => {
  const { t } = useTranslation();
  const country = useSelector(reportFilterCountryCodeSelector);
  const { loaded, data: totals } = useSelector(
    reportMissingClosingTotalMissingClosingTotalSelector
  );

  let title = t('report-tender.title');

  if (!constants.isSmsReportedCountry(country) && loaded && totals !== null) {
    title += ` (${totals} ${t('sale-totals.labels.missing')})`;
  }
  return <span>{title}</span>;
};

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<TenderReport, ReportTenderDataGridContext>();
  const { t } = useTranslation();
  const titles = useMemo(
    () => ({ title: t('report-tender.title'), tableTitle: <DataGridTitle /> }),
    [t]
  );

  const country = useSelector(reportFilterCountryCodeSelector);
  const tenderResource = useSelector(reportTenderSelector);
  const selectedGroupBy = useSelector(reportFilterTenderGroupBySelector);
  const groupBy = (
    constants.isSmsReportedCountry(country) ? constants.tenderSmsGroupBy : selectedGroupBy
  ) as ReportFilterTenderGroupBy[];
  const masterData = useSelector(masterDataDataSelector);

  const { data: saleTypeOptions } = useSelector(reportSalesTypesSelector);

  const { loading, data } = tenderResource;

  const {
    agreements = [],
    companies = [],
    managements = [],
    regionalManagements = [],
    regions = [],
    supervisions = [],
  } = masterData || {};

  const gridContext = useMemo(
    () => ({
      t,
      country,
      groupBy,
      saleTypeOptions,
      agreements,
      companies,
      managements,
      regionalManagements,
      regions,
      supervisions,
    }),
    [
      t,
      country,
      groupBy,
      saleTypeOptions,
      agreements,
      companies,
      managements,
      regionalManagements,
      regions,
      supervisions,
    ]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title: titles,
    loading,
  });

  // TEMP-DEBUG: quitar
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[tender-debug]', {
      country,
      groupBy,
      loading,
      dataLength: data.length,
      handlerRows: handler.getRows().length,
      visibleColumns: handler.getVisibleColumns().map(c => c.id),
      hidden: [...handler.columnsHidden],
    });
  }, [handler, country, groupBy, loading, data]);

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<TenderReport, ReportTenderDataGridContext>();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startAccountingDate',
                'endAccountingDate',
                'startOperationDate',
                'endOperationDate',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t]);

  return null;
};

const ReportTenderDataGrid = () => (
  <DataGridFlexSales
    handlerFactory={handlerFactory}
    columnChooserDisabledSelector={columnChooserDisabledSelector}
  >
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default ReportTenderDataGrid;
